import Header from '@/react-app/components/Header';
import Footer from '@/react-app/components/Footer';
import { Shield, Eye, Lock, Database, Mail, Phone } from 'lucide-react';

export default function Privacy() {
    return (
        <div className="min-h-screen bg-white dark:bg-gray-900">
            <Header />

            {/* Hero Section */}
            <section className="pt-16 bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                    <div className="text-center mb-12">
                        <div className="bg-blue-100 dark:bg-blue-900 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                            <Shield className="h-10 w-10 text-blue-600 dark:text-blue-400" />
                        </div>
                        <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6">
                            Privacy Policy
                        </h1>
                        <p className="text-xl text-gray-600 dark:text-gray-300 leading-relaxed">
                            Your privacy is fundamental to our service. Learn how we protect and handle your personal information.
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                            Last updated: September 27, 2025
                        </p>
                    </div>
                </div>
            </section>

            {/* Content */}
            <section className="py-16">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="prose prose-lg max-w-none dark:prose-invert">

                        {/* Introduction */}
                        <div className="mb-12">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                                <Eye className="h-6 w-6 text-blue-600 dark:text-blue-400 mr-3" />
                                Introduction
                            </h2>
                            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                                Trade Enrich ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our savings and investment platform, including our website and mobile applications (collectively, the "Service").
                            </p>
                        </div>

                        {/* Information We Collect */}
                        <div className="mb-12">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                                <Database className="h-6 w-6 text-blue-600 dark:text-blue-400 mr-3" />
                                Information We Collect
                            </h2>

                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Personal Information</h3>
                                    <p className="text-gray-600 dark:text-gray-300 mb-3">We may collect personal information that you provide directly to us, including:</p>
                                    <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-2 ml-4">
                                        <li>Name, email address, phone number, and date of birth</li>
                                        <li>Government-issued identification for KYC verification</li>
                                        <li>Banking and payment information</li>
                                        <li>Investment preferences and financial goals</li>
                                        <li>Communication preferences and correspondence</li>
                                    </ul>
                                </div>

                                <div>
                                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Automatically Collected Information</h3>
                                    <p className="text-gray-600 dark:text-gray-300 mb-3">When you use our Service, we automatically collect:</p>
                                    <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-2 ml-4">
                                        <li>Device information (IP address, browser type, operating system)</li>
                                        <li>Usage data (pages visited, time spent, features used)</li>
                                        <li>Location data (with your permission)</li>
                                        <li>Cookies and similar tracking technologies</li>
                                    </ul>
                                </div>

                                <div>
                                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Third-Party Information</h3>
                                    <p className="text-gray-600 dark:text-gray-300">We may receive information about you from third parties, including identity verification services, credit reporting agencies, and financial institutions to verify your identity and prevent fraud.</p>
                                </div>
                            </div>
                        </div>

                        {/* How We Use Information */}
                        <div className="mb-12">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">How We Use Your Information</h2>
                            <p className="text-gray-600 dark:text-gray-300 mb-4">We use the information we collect to:</p>
                            <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-2 ml-4">
                                <li>Provide, maintain, and improve our financial services</li>
                                <li>Process transactions and manage your account</li>
                                <li>Verify your identity and comply with regulatory requirements</li>
                                <li>Communicate with you about your account and our services</li>
                                <li>Detect, prevent, and address fraud and security issues</li>
                                <li>Analyze usage patterns to enhance user experience</li>
                                <li>Send marketing communications (with your consent)</li>
                                <li>Comply with legal obligations and regulatory requirements</li>
                            </ul>
                        </div>

                        {/* Information Sharing */}
                        <div className="mb-12">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Information Sharing and Disclosure</h2>
                            <p className="text-gray-600 dark:text-gray-300 mb-4">We may share your information in the following circumstances:</p>

                            <div className="space-y-4">
                                <div>
                                    <h4 className="font-semibold text-gray-900 dark:text-white">Service Providers</h4>
                                    <p className="text-gray-600 dark:text-gray-300">With third-party vendors who provide services on our behalf, including payment processing, identity verification, and customer support.</p>
                                </div>

                                <div>
                                    <h4 className="font-semibold text-gray-900 dark:text-white">Legal Requirements</h4>
                                    <p className="text-gray-600 dark:text-gray-300">When required by law, regulation, or legal process, including compliance with anti-money laundering and know-your-customer requirements.</p>
                                </div>

                                <div>
                                    <h4 className="font-semibold text-gray-900 dark:text-white">Business Transfers</h4>
                                    <p className="text-gray-600 dark:text-gray-300">In connection with any merger, sale of company assets, financing, or acquisition of all or a portion of our business.</p>
                                </div>

                                <div>
                                    <h4 className="font-semibold text-gray-900 dark:text-white">Consent</h4>
                                    <p className="text-gray-600 dark:text-gray-300">With your explicit consent or at your direction.</p>
                                </div>
                            </div>
                        </div>

                        {/* Data Security */}
                        <div className="mb-12">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                                <Lock className="h-6 w-6 text-blue-600 dark:text-blue-400 mr-3" />
                                Data Security
                            </h2>
                            <p className="text-gray-600 dark:text-gray-300 mb-4">
                                We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. Our security measures include:
                            </p>
                            <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-2 ml-4">
                                <li>256-bit SSL encryption for data transmission</li>
                                <li>AES-256 encryption for data at rest</li>
                                <li>Multi-factor authentication requirements</li>
                                <li>Regular security audits and penetration testing</li>
                                <li>Employee training on data protection practices</li>
                                <li>Restricted access to personal information on a need-to-know basis</li>
                            </ul>
                        </div>

                        {/* Your Rights */}
                        <div className="mb-12">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Your Privacy Rights</h2>
                            <p className="text-gray-600 dark:text-gray-300 mb-4">Depending on your location, you may have the following rights:</p>
                            <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-2 ml-4">
                                <li><strong>Access:</strong> Request access to your personal information</li>
                                <li><strong>Correction:</strong> Request correction of inaccurate information</li>
                                <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                                <li><strong>Portability:</strong> Request a copy of your data in a portable format</li>
                                <li><strong>Objection:</strong> Object to certain processing of your information</li>
                                <li><strong>Restriction:</strong> Request restriction of processing in certain circumstances</li>
                            </ul>
                            <p className="text-gray-600 dark:text-gray-300 mt-4">
                                To exercise these rights, please contact us using the information provided in the "Contact Us" section below.
                            </p>
                        </div>

                        {/* Data Retention */}
                        <div className="mb-12">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Data Retention</h2>
                            <p className="text-gray-600 dark:text-gray-300">
                                We retain your personal information for as long as necessary to provide our services, comply with legal obligations, resolve disputes, and enforce our agreements. Specific retention periods vary depending on the type of information and applicable legal requirements. Financial transaction records are typically retained for seven years in accordance with regulatory requirements.
                            </p>
                        </div>

                        {/* International Transfers */}
                        <div className="mb-12">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">International Data Transfers</h2>
                            <p className="text-gray-600 dark:text-gray-300">
                                Your information may be transferred to and processed in countries other than your country of residence. We ensure that such transfers are made in accordance with applicable data protection laws and implement appropriate safeguards to protect your information.
                            </p>
                        </div>

                        {/* Children's Privacy */}
                        <div className="mb-12">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Children's Privacy</h2>
                            <p className="text-gray-600 dark:text-gray-300">
                                Our Service is not intended for individuals under the age of 18. We do not knowingly collect personal information from children under 18. If we become aware that we have collected personal information from a child under 18, we will take steps to delete such information.
                            </p>
                        </div>

                        {/* Changes to Policy */}
                        <div className="mb-12">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Changes to This Privacy Policy</h2>
                            <p className="text-gray-600 dark:text-gray-300">
                                We may update this Privacy Policy from time to time to reflect changes in our practices or applicable laws. We will notify you of any material changes by posting the updated policy on our website and updating the "Last updated" date. Your continued use of our Service after such changes constitutes acceptance of the updated policy.
                            </p>
                        </div>

                        {/* Contact Information */}
                        <div className="mb-12">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                                <Mail className="h-6 w-6 text-blue-600 dark:text-blue-400 mr-3" />
                                Contact Us
                            </h2>
                            <div className="bg-blue-50 dark:bg-blue-900/20 p-6 rounded-lg">
                                <p className="text-gray-600 dark:text-gray-300 mb-4">
                                    If you have any questions about this Privacy Policy or our privacy practices, please contact us:
                                </p>
                                <div className="space-y-2 text-gray-600 dark:text-gray-300">
                                    <div className="flex items-center">
                                        <Mail className="h-4 w-4 text-blue-600 dark:text-blue-400 mr-2" />
                                        <span>Email: support@tradeenrich.com</span>
                                    </div>
                                    <div className="flex items-center">
                                        <Phone className="h-4 w-4 text-blue-600 dark:text-blue-400 mr-2" />
                                        <span>Phone: +234 7063441554</span>
                                    </div>
                                    <div className="mt-4">
                                        <p className="font-medium">Mailing Address:</p>
                                        <p>Trade Enrich Privacy Team<br />
                                            Thomas and Paulina Ibe's New Compound by Nkwo Mbaise Market Umuodo Itu Autonomous Community Ezinihitte-Mbaise LGA, Imo State, Nigeria<br />
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
